import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnFiltersState,
  VisibilityState,
} from "@tanstack/react-table";
import { Input } from "@/components/ui/input";

import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useAppDispatch } from "@/redux/hooks";
import {
  getFeedbacks,
  updateFeedbacks,
} from "@/redux/feedback/feedback.action";
import { Feedback } from "@/redux/feedback/types/Feedback.interface";
import { Typography } from "@mui/material";
import { RequestStatus } from "@/utilities/ResponseEnum/RequestStatus";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { useState } from "react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  columnsResolved: ColumnDef<TData, TValue>[];
  dataNotResolved: TData[];
  dataResolved: TData[];
}

const DataTableFeedback = <TData, TValue>({
  columns,
  columnsResolved,
  dataNotResolved,
  dataResolved,
}: DataTableProps<TData, TValue>) => {
  const dispatch = useAppDispatch();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const tableResolved = useReactTable({
    data: Array.isArray(dataResolved) ? dataResolved : [],
    columns: columnsResolved,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 3,
      },
    },
  });
  const tableNotResolved = useReactTable({
    data: Array.isArray(dataNotResolved) ? dataNotResolved : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 3,
      },
    },
  });

  const columnLabels: Record<string, string> = {
    type: "Tipo",
    message: "Mensaje",
    date: "Fecha",
    images: "Imágenes",
    user: "Usuario",
  };

  const handleResolve = async (feedbacks: Feedback[]) => {
    const response = await dispatch(updateFeedbacks(feedbacks));
    if (response.meta.requestStatus === RequestStatus.Fulfilled) {
      SuccessToast({ message: "Feedback resuelto correctamente" });
      dispatch(getFeedbacks());
    } else {
      ErrorToast({ message: "Error al resolver feedback" });
    }
  };

  return (
    <div className="flex flex-col max-h-[550px]">
      <div className="flex items-center py-4 justify-between">
        <Input
          placeholder="Buscar por tipo..."
          value={tableNotResolved.getColumn("type")?.getFilterValue() as string}
          onChange={(event) =>
            tableNotResolved
              .getColumn("type")
              ?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button
              variant="outline"
              className="ml-auto border-gray-200 hover:bg-gray-200 text-black shadow-sm"
            >
              Columnas <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {tableNotResolved
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize hover:bg-gray-100"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {columnLabels[column.id as keyof typeof columnLabels]}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div>
        <div className="h-fit min-h-[200px] overflow-y-auto">
          <Table>
            <TableHeader>
              {tableNotResolved.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={
                          header.id === "actions"
                            ? "w-[50px] max-w-[50px] truncate"
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {tableNotResolved.getRowModel().rows?.length ? (
                tableNotResolved.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className={
                      row.id === "actions"
                        ? "w-[50px] max-w-[50px] truncate"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No hay resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between space-x-2 py-4 w-full">
          <div className="flex items-center space-x-2">
            <Button
              disabled={
                tableNotResolved.getSelectedRowModel().rows.length === 0
              }
              variant={"outline"}
              size="sm"
              className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              onClick={() => {
                const feedbacksResolved = tableNotResolved
                  .getSelectedRowModel()
                  .rows.map((row) => row.original as Feedback)
                  .map((feedback) => ({
                    ...feedback,
                    isResolved: true,
                  }));
                handleResolve(feedbacksResolved);
              }}
            >
              Resolver
            </Button>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={"outline"}
              className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              size="sm"
              onClick={() => tableNotResolved.previousPage()}
              disabled={!tableNotResolved.getCanPreviousPage()}
            >
              Anterior
            </Button>
            <Button
              variant={"outline"}
              className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              size="sm"
              onClick={() => tableNotResolved.nextPage()}
              disabled={!tableNotResolved.getCanNextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
      <div className="flex items-center py-2 justify-between">
        <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={600}>
          Feedback Resueltos
        </Typography>
      </div>
      <div className="h-fit min-h-[200px] overflow-y-auto">
        <Table>
          <TableHeader>
            {tableResolved.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={
                        header.id === "actions"
                          ? "w-[50px] max-w-[50px] truncate"
                          : ""
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {tableResolved.getRowModel().rows?.length ? (
              tableResolved.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={
                    row.id === "actions" ? "w-[50px] max-w-[50px] truncate" : ""
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No hay resultados.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between space-x-2 py-4 w-full">
        <Button
          variant={"outline"}
          className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
          size="sm"
          onClick={() => tableResolved.previousPage()}
          disabled={!tableResolved.getCanPreviousPage()}
        >
          Anterior
        </Button>
        <Button
          variant={"outline"}
          className="ml-auto border-gray-200 hover:bg-gray-100 text-black shadow-sm"
          size="sm"
          onClick={() => tableResolved.nextPage()}
          disabled={!tableResolved.getCanNextPage()}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};

export default DataTableFeedback;
